export default function spotlight({
  stageRef,
  overlayRef,
  anchorRef,
  sec1Ref,
  sec2Ref,
}) {
  const getEl = (r) => (r && r.current) || null;
  const vh = () =>
    window.visualViewport?.height ||
    window.innerHeight ||
    document.documentElement.clientHeight;
  const vw = () => window.innerWidth || document.documentElement.clientWidth;

  let ghost = null;
  let activeSprite = null;
  let rafId = 0;
  let io;
  let warming = false;

  let requestPending = false;
  let requestAnimate = true;
  let creating = false;

  const stageEl = getEl(stageRef);
  const overlayEl = getEl(overlayRef);
  const anchorEl = getEl(anchorRef);
  const sec1El = getEl(sec1Ref);
  const sec2El = getEl(sec2Ref);
  if (!stageEl || !overlayEl || !anchorEl || !sec1El || !sec2El)
    return () => {};

  const isPortrait = () => window.matchMedia("(orientation: portrait)").matches;
  const isPhonePortrait = () => vw() <= 768 && isPortrait();
  const isTabletPortrait = () => vw() > 768 && vw() <= 1024 && isPortrait();

  const sprites = () => Array.from(stageEl.querySelectorAll(".sprite"));
  const hasSprites = () => sprites().length > 0;

  const isMostlyVisible = (el, ratio = 0.55) => {
    const r = el.getBoundingClientRect();
    const H = vh();
    const visible = Math.max(0, Math.min(r.bottom, H) - Math.max(r.top, 0));
    return visible / Math.min(H, r.height || H) >= ratio;
  };

  const computeTargetRect = () => {
    const a = anchorEl.getBoundingClientRect();
    const overlayRect = overlayEl.getBoundingClientRect();
    const ratio = activeSprite
      ? parseFloat(activeSprite.dataset.ratio || "0.6")
      : 0.6;

    let baseVh, maxHpx, maxWvw;
    if (isPhonePortrait()) {
      baseVh = 0.34;
      maxHpx = 280;
      maxWvw = 42;
    } else if (isTabletPortrait()) {
      baseVh = 0.38;
      maxHpx = 360;
      maxWvw = 36;
    } else {
      baseVh = 0.5;
      maxHpx = 520;
      maxWvw = 28;
    }

    let h = Math.min(Math.round(vh() * baseVh), maxHpx);
    let wPx = Math.round(h * ratio);
    const wCap = Math.round((maxWvw / 100) * vw());
    if (wPx > wCap) {
      wPx = wCap;
      h = Math.round(wPx / ratio);
    }

    return {
      left: a.left - overlayRect.left,
      top: a.top - overlayRect.top - h / 2 + a.height / 2,
      width: wPx,
      height: h,
    };
  };

  const follow = () => {
    if (!ghost || !anchorEl || !anchorEl.isConnected) return;
    const t = computeTargetRect();
    Object.assign(ghost.style, {
      left: t.left + "px",
      top: t.top - 10 + "px",
      width: t.width + "px",
      height: t.height + "px",
    });
    rafId = requestAnimationFrame(follow);
  };

  const clear = () => {
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
    stageEl.classList.remove("spotlight");
    // Inline style removal is reliable in Safari (class-based filter removal is not)
    stageEl.style.removeProperty("filter");
    stageEl.style.removeProperty("-webkit-filter");
    stageEl.style.removeProperty("opacity");
    stageEl.style.removeProperty("will-change");
    if (ghost) {
      ghost.classList.remove("zoomed");
      ghost.remove();
    }
    if (activeSprite?.style) activeSprite.style.opacity = "";
    ghost = null;
    activeSprite = null;
  };

  const ensureGhost = () => {
    if (ghost?.isConnected) return ghost;
    const existing = overlayEl.querySelector(".spotlight-ghost");

    // Ensure pill styles exist globally
    let style = document.getElementById("spotlight-pill-style");
    if (!style) {
      style = document.createElement("style");
      style.id = "spotlight-pill-style";
      style.textContent = `
        .spotlight-ghost.zoomed .profile-pill[data-side="right"] {
          opacity: 1 !important;
          transform: translateY(-50%) translateX(-5%) !important;
        }
        .spotlight-ghost.zoomed .profile-pill[data-side="left"] {
          opacity: 1 !important;
          transform: translateY(-50%) translateX(-15%) !important;
        }
        @media (max-width: 1280px) {
          .profile-pill { display: none !important; }
        }
      `;
      document.head.appendChild(style);
    }

    if (!existing) {
      ghost = document.createElement("div");
      ghost.className = "spotlight-ghost";
      ghost.style.cssText = `
        position:absolute;
        filter:drop-shadow(0 10px 18px rgba(0,0,0,.25));
        z-index:3; pointer-events:none; opacity:0;
        transition:left .55s cubic-bezier(.2,.7,.2,1),
                   top .55s cubic-bezier(.2,.7,.2,1),
                   width .55s cubic-bezier(.2,.7,.2,1),
                   height .55s cubic-bezier(.2,.7,.2,1),
                   opacity .25s ease;
        will-change:left,top,width,height,opacity;
        display: flex;
        justify-content: center;
        align-items: center;
      `;

      const img = document.createElement("img");
      img.className = "ghost-img";
      img.style.cssText = "width: 100%; height: 100%; object-fit: contain;";
      ghost.appendChild(img);

      // Pills positioned at specific body landmarks, alternating sides.
      const pillsData = [
        { text: "High Achiever", top: "10%", side: "right" }, // head, right
        { text: "Verified Skills", top: "30%", side: "left" }, // shoulder, left
        { text: "Strong Fit", top: "45%", side: "right" }, // elbow, right
      ];

      pillsData.forEach(({ text, top, side }, idx) => {
        const pill = document.createElement("div");
        pill.className = "profile-pill";
        pill.dataset.side = side;
        pill.textContent = text;

        const isRight = side === "right";
        const edgeProp = isRight ? "right" : "left";
        const revealTransform = isRight
          ? "translateY(-50%) translateX(100%)"
          : "translateY(-50%) translateX(-100%)";

        pill.style.cssText = `
          position: absolute;
          ${edgeProp}: -16px;
          top: ${top};
          transform: ${revealTransform};
          background: rgba(255,255,255,0.97);
          border: 1px solid rgba(210,210,210,0.5);
          box-shadow: 0 4px 16px rgba(0,0,0,0.1);
          border-radius: 20px;
          padding: 6px 14px;
          font-size: 11px;
          font-weight: 700;
          color: #111;
          white-space: nowrap;
          opacity: 0;
          transition: opacity 0.35s ease ${0.2 + idx * 0.1}s,
                      transform 0.4s cubic-bezier(0.2, 0.8, 0.2, 1) ${0.2 + idx * 0.1}s;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          z-index: 20;
          pointer-events: none;
        `;
        ghost.appendChild(pill);
      });

      overlayEl.appendChild(ghost);
    } else {
      ghost = existing;
    }
    return ghost;
  };

  const requestSpotlight = (animate) => {
    requestAnimate = requestPending ? requestAnimate && animate : animate;
    if (requestPending) return;
    requestPending = true;
    queueMicrotask(() => {
      requestPending = false;
      spotlightIn(requestAnimate);
    });
  };

  const spotlightIn = (animate = true) => {
    if (ghost || creating) return;
    creating = true;

    const list = sprites();
    const vis = list.filter((el) => {
      const r = el.getBoundingClientRect();
      return r.bottom > 0 && r.top < vh();
    });
    activeSprite = (vis.length ? vis : list)[
      Math.floor(Math.random() * (vis.length || list.length))
    ];
    if (!activeSprite) {
      creating = false;
      return;
    }

    const sr = activeSprite.getBoundingClientRect();
    const overlayRect = overlayEl.getBoundingClientRect();
    const img = activeSprite.querySelector("img");
    const g = ensureGhost();
    const ghostImg = g.querySelector(".ghost-img") || g;
    if (img?.src) ghostImg.src = img.src;

    stageEl.classList.add("spotlight");
    // Use inline styles for blur on Safari — class-based filter is unreliable on WebKit
    stageEl.style.setProperty("filter", "blur(8px)");
    stageEl.style.setProperty("-webkit-filter", "blur(8px)");
    stageEl.style.setProperty("opacity", "0.5");
    stageEl.style.setProperty("will-change", "filter");
    activeSprite.style.opacity = "0";

    if (!animate) {
      const t = computeTargetRect();
      const prev = g.style.transition;
      g.style.transition = "none";
      Object.assign(g.style, {
        opacity: "1",
        left: t.left + "px",
        top: t.top + "px",
        width: t.width + "px",
        height: t.height + "px",
      });
      g.classList.add("zoomed");
      // force reflow
      // eslint-disable-next-line no-unused-expressions
      g.offsetWidth;
      g.style.transition = prev;
      rafId = requestAnimationFrame(follow);
      creating = false;
      return;
    }

    Object.assign(g.style, {
      left: sr.left - overlayRect.left + "px",
      top: sr.top - overlayRect.top + "px",
      width: sr.width + "px",
      height: sr.height + "px",
      opacity: "1",
    });
    rafId = requestAnimationFrame(follow);

    const t = computeTargetRect();
    requestAnimationFrame(() => {
      Object.assign(g.style, {
        left: t.left + "px",
        top: t.top + "px",
        width: t.width + "px",
        height: t.height + "px",
      });
      g.classList.add("zoomed");
      creating = false;
    });
  };

  const spotlightOut = () => {
    // Remove inline blur inline — reliable on Safari
    stageEl.style.removeProperty("filter");
    stageEl.style.removeProperty("-webkit-filter");
    stageEl.style.removeProperty("opacity");
    stageEl.style.removeProperty("will-change");

    if (!ghost || !activeSprite) {
      stageEl.classList.remove("spotlight");
      if (ghost) ghost.classList.remove("zoomed");
      return;
    }
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;

    ghost.classList.remove("zoomed");

    const spriteAtStart = activeSprite;
    const ghostAtStart = ghost;
    const dest = spriteAtStart.getBoundingClientRect();
    const overlayRect = overlayEl.getBoundingClientRect();
    Object.assign(ghostAtStart.style, {
      left: dest.left - overlayRect.left + "px",
      top: dest.top - overlayRect.top + "px",
      width: dest.width + "px",
      height: dest.height + "px",
    });

    const onEnd = () => {
      try {
        spriteAtStart.style.opacity = "";
      } finally {
        clear();
      }
    };
    ghostAtStart.addEventListener("transitionend", onEnd, { once: true });
  };

  const warmStartIfNeeded = () => {
    if (warming || !isMostlyVisible(sec2El, 0.55)) return;
    warming = true;
    stageEl.classList.add("spotlight");

    const tryStart = () => {
      if (hasSprites()) {
        requestSpotlight(false);
        return;
      }
      let tries = 0;
      const tick = () => {
        if (hasSprites()) requestSpotlight(false);
        else if (tries++ < 90) requestAnimationFrame(tick);
        else {
          stageEl.classList.remove("spotlight");
          warming = false;
        }
      };
      requestAnimationFrame(tick);
    };
    tryStart();
  };

  io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.target === sec2El) {
          if (e.isIntersecting && e.intersectionRatio > 0.55)
            requestSpotlight(true);
          else if (e.intersectionRatio < 0.45) spotlightOut();
        }
        if (
          e.target === sec1El &&
          e.isIntersecting &&
          e.intersectionRatio > 0.55
        ) {
          spotlightOut();
        }
      }
    },
    { threshold: [0, 0.45, 0.55, 1] },
  );

  io.observe(sec1El);
  io.observe(sec2El);

  requestAnimationFrame(warmStartIfNeeded);
  const onResize = () => warmStartIfNeeded();
  window.addEventListener("orientationchange", onResize, { passive: true });
  window.addEventListener("resize", onResize, { passive: true });

  return () => {
    io?.disconnect();
    window.removeEventListener("orientationchange", onResize);
    window.removeEventListener("resize", onResize);
    clear();
  };
}
